import { z } from 'zod'

/**
 * Export Data Schema - Type-specific structure for JSON export
 * Uses flattened properties and type-specific arrays instead of generic objects array
 */

// Position as [x, y] tuple (compatible with router API)
const PositionSchema = z.tuple([z.number(), z.number()])

// Points array for polygon/polyline geometries (min 3 for polygons)
const PointsSchema = z.array(z.tuple([z.number(), z.number()])).min(3)

// Points array for any geometry (min 1 for point-based objects that wrap their position)
const FlexPointsSchema = z.array(z.tuple([z.number(), z.number()])).min(1)

/**
 * CCTV Schemas
 */
export const LightCCTVSchema = z.object({
  id: z.coerce.string().min(1), // DB/콘솔 왕복본과 동일하게 문자열 id 로 내보낸다
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  name: z.string().optional(),
  serialNumber: z.string().optional(),
  ipAddress: z.string().optional(),
  model: z.string().optional(),
  resolution: z.string().optional(),
  angle: z.number().optional(), // properties.angle 보존 (값만 저장, 회전 적용 X)
  linkedParkingLocationIds: z.array(z.string()).optional(),
  linkedEmergencyBellIds: z.array(z.string()).optional()
}).passthrough() // Allow additional properties for dynamic relationships

// 단방향 CCTV — 조명 CCTV 와 달리 모니터링 관계가 없어 필드가 단순하다.
export const WarningCCTVSchema = z.object({
  id: z.coerce.string().min(1),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  name: z.string().optional(),
  ipAddress: z.string().optional(),
  angle: z.number().optional(),
}).passthrough()

export const CCTVsSchema = z.object({
  lightCctvs: z.array(LightCCTVSchema),
  warningCctvs: z.array(WarningCCTVSchema).optional(),
})

/**
 * Zone Schema
 */
export const ZoneSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: PointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  zoneName: z.string().optional(),
  capacity: z.number().optional(),
  handicappedSpots: z.number().optional(),
  angle: z.number().optional(), // properties.angle 보존
  columnIds: z.array(z.string()).optional(),
  parkingLocationIds: z.array(z.string()).optional(),
  includedParkingLocationIds: z.array(z.string()).optional() // Router API 호환
}).passthrough()

/**
 * Column Schema
 */
export const ColumnSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: PointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  label: z.string().optional(), // Router API 호환
  angle: z.number().optional(), // properties.angle 보존
  zoneId: z.string().optional()
}).passthrough()

/** Parking location type (required for router/DB) */
export const ParkingLocationTypeEnum = z.enum([
  'REGULAR',
  'HANDICAPPED',
  'WOMEN_ONLY',
  'COMPACT',
  'ELECTRIC',
  'FAMILY',
  'VULNERABLE',
  'MOTORCYCLE'
])

/**
 * ParkingLocation Schema
 * `type` is required for v3 map API / router DB (parking_locations.type).
 */
export const ParkingLocationSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: PointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  type: ParkingLocationTypeEnum.default('REGULAR'),
  angle: z.number().optional(), // Router API 호환
  spotNumber: z.string().optional(),
  spotType: ParkingLocationTypeEnum.optional(),
  status: z.enum(['available', 'occupied', 'reserved']).optional(),
  monitoringCctvs: z.array(z.string()).optional(), // Router API 호환
  zoneId: z.string().optional(),
  chargerId: z.string().optional(),
  sensorId: z.string().optional()
}).passthrough()

/**
 * Sensor Schema
 */
export const SensorSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  sensorType: z.string().optional(),
  model: z.string().optional(),
  range: z.number().optional(),
  angle: z.number().optional(), // properties.angle 보존
  linkedSpotId: z.string().optional() // Relation to parking spot
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * Charger Schema
 */
export const ChargerSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  powerOutput: z.number().optional(),
  chargingType: z.string().optional(),
  connectorType: z.string().optional(),
  status: z.enum(['available', 'occupied', 'offline']).optional(),
  angle: z.number().optional(), // properties.angle 보존
  parkingSpotId: z.string().optional() // Relation to parking spot
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * GuideBoard Schema
 */
export const GuideBoardSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  ipAddress: z.string().optional(), // Router API 호환
  content: z.string().optional(),
  direction: z.string().optional(),
  angle: z.number().optional(), // properties.angle 보존
  leftDirectionIds: z.array(z.string()).optional(), // Router API 호환
  rightDirectionIds: z.array(z.string()).optional() // Router API 호환
}).passthrough()

/**
 * Elevator Schema
 */
export const ElevatorSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  floors: z.array(z.string()).optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * EmergencyBell Schema
 */
export const EmergencyBellSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: z.tuple([z.number(), z.number()]),
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  linkedCctvId: z.string().optional(),
  linkedCustomId: z.string().optional(), // Router API 호환
  angle: z.number().optional() // properties.angle 보존
}).passthrough()

/**
 * Arrow Schema
 */
export const ArrowSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: z.array(z.tuple([z.number(), z.number()])).min(2),
  width: z.number().optional(),
  height: z.number().optional(),
  direction: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * OutLine/InnerLine Schema (shared)
 */
export const LineSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: z.array(z.tuple([z.number(), z.number()])).min(2),
  width: z.number().optional(),
  height: z.number().optional(),
  lineType: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * Entrance Schema
 */
export const EntranceSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  entranceType: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * OnePassReader Schema
 */
export const OnePassReaderSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  readerId: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * OccupancyLight Schema
 */
export const OccupancyLightSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: FlexPointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  status: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * Light Schema
 */
export const LightSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  position: PositionSchema,
  points: PointsSchema,
  width: z.number().optional(),
  height: z.number().optional(),
  lightType: z.string().optional(),
  angle: z.number().optional() // properties.angle 보존
}).passthrough() // Allow additional properties for dynamic relationships

/**
 * CustomObject Schema
 *
 * 표준 카테고리(cctv/zone/parkingLocation/...)로 라우팅되지 않는, 사용자가
 * 맵에디터에서 새로 만든 임의 레이어 객체(예: 'dongs' 동 라벨, 커스텀 텍스트).
 * export JSON 에서는 이 객체들을 레이어 이름을 키로 하는 최상위 배열로 묶는다
 * (예: { "dongsk": CustomObject[] }). 기존에는 routeLayerToCategory 가 'unknown'
 * 을 반환해 export 에서 통째로 누락되던 것을 보존하기 위해 추가.
 *
 * - position: 객체 좌상단 [x, y]
 * - size: 바운딩 박스 { width, height }
 * - points: 사각형 꼭지점(라벨/아이콘) 또는 실제 폴리곤 정점
 */
export const CustomObjectSchema = z.object({
  id: z.coerce.string().min(1),
  name: z.string().optional(),
  layer: z.string().optional(),
  type: z.string().optional(),
  position: PositionSchema,
  size: z.object({ width: z.number(), height: z.number() }),
  points: FlexPointsSchema,
  angle: z.number().optional(), // properties.angle 보존
}).passthrough() // 동적 properties/관계 보존

/**
 * MapData Export Schema - Type-specific arrays
 */
export const MapDataExportSchema = z.object({
  cctvs: CCTVsSchema,
  zones: z.array(ZoneSchema),
  columns: z.array(ColumnSchema),
  parkingLocations: z.array(ParkingLocationSchema),
  sensors: z.array(SensorSchema),
  chargers: z.array(ChargerSchema),
  guideBoards: z.array(GuideBoardSchema),
  elevators: z.array(ElevatorSchema),
  emergencyBells: z.array(EmergencyBellSchema),
  arrows: z.array(ArrowSchema),
  outLines: z.array(LineSchema),
  innerLines: z.array(LineSchema),
  entrances: z.array(EntranceSchema),
  onePassReaders: z.array(OnePassReaderSchema),
  occupancyLights: z.array(OccupancyLightSchema),
  lights: z.array(LightSchema),
  carChargers: z.array(ChargerSchema),
}).passthrough() // 사용자 정의 레이어 객체는 레이어 이름 키(예: 'dongsk': CustomObject[])로 최상위에 추가되므로 strip 하지 않고 통과시킨다.

/**
 * Parking Lot Level Schema
 */
export const ParkingLotLevelSchema = z.object({
  name: z.string().min(1, 'Floor name is required'),
  mapData: MapDataExportSchema
})

/**
 * Export Data Schema - Top-level wrapper
 */
export const ExportDataSchema = z.object({
  data: z.object({
    createdAt: z.string().datetime('Created timestamp must be a valid ISO 8601 datetime'),
    name: z.string().min(1, 'Project name is required'),
    parkingLotLevels: z.array(ParkingLotLevelSchema)
      .min(1, 'At least one floor is required')
  })
})

/**
 * TypeScript types inferred from schemas
 */
export type Position = z.infer<typeof PositionSchema>
export type LightCCTV = z.infer<typeof LightCCTVSchema>
export type WarningCCTV = z.infer<typeof WarningCCTVSchema>
export type CCTVs = z.infer<typeof CCTVsSchema>
export type Zone = z.infer<typeof ZoneSchema>
export type Column = z.infer<typeof ColumnSchema>
export type ParkingLocation = z.infer<typeof ParkingLocationSchema>
export type Sensor = z.infer<typeof SensorSchema>
export type Charger = z.infer<typeof ChargerSchema>
export type GuideBoard = z.infer<typeof GuideBoardSchema>
export type Elevator = z.infer<typeof ElevatorSchema>
export type EmergencyBell = z.infer<typeof EmergencyBellSchema>
export type Arrow = z.infer<typeof ArrowSchema>
export type Line = z.infer<typeof LineSchema>
export type Entrance = z.infer<typeof EntranceSchema>
export type OnePassReader = z.infer<typeof OnePassReaderSchema>
export type OccupancyLight = z.infer<typeof OccupancyLightSchema>
export type Light = z.infer<typeof LightSchema>
export type CustomObject = z.infer<typeof CustomObjectSchema>
export type MapDataExport = z.infer<typeof MapDataExportSchema>
export type ParkingLotLevel = z.infer<typeof ParkingLotLevelSchema>
export type ExportData = z.infer<typeof ExportDataSchema>
